import { Component, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { ToastrService } from 'ngx-toastr';
import { SubscriptionService } from 'src/app/private/services/subscription.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { UsersService } from 'src/app/private/services/users.service';
import { PermissionService } from 'src/app/private/services/permission.service';
import { SubscriptionBillingLockService } from 'src/app/private/services/subscription-billing-lock.service';
import { clearActiveUserPackageCache } from 'src/app/utils/active-user-package-cache.util';
import { parseApiErrorPayload } from 'src/app/utils/subscription-api-error.util';
import * as XLSX from 'xlsx';

interface UsageMetric {
  name: string;
  key: string;
  current: number;
  limit: number;
  type:string;
  unit: string;
  icon: string;
  color: string;
}

interface Invoice {
  invoice_id: string;
  client_id: string;
  package_subscription_id: string;
  invoice_no: string;
  invoice_type: string;
  invoice_origin: string;
  billing_context: string;
  status: string;
  amount: string;
  tax_amount: string;
  total_amount: string;
  is_inter_state: boolean;
  invoice_url: string;
  created_at: string;
  issued_at: string;
  paid_at?: string;
  payment_link_expires_at?: string;
}

interface Addon {
  id: string;
  name: string;
  description: string;
  price: any;
  is_active: boolean;
  quantity: number;
  feature: {
    fixed: any[];
    limits: Array<{
      key: string;
      label: string;
      value: number;
      enabled: boolean;
      isLimit: boolean;
      unit_type: string;
    }>;
  };
  createdAt: string;
  updatedAt: string;
  icon?: string;
  popular?: boolean;
  activeTab?: 'features' | 'credits';
}

interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  monthlyPrice: number;
  isTrial: boolean;
  visibility: string;
  trialPeriod: number;  
  yearlyPrice: number;
  features: string[];
  fixedFeatures: string[];  // Features tab content
  limitsFeatures: string[]; // Credits/Limits tab content
  limits: {
    users: number;
    storage: number;
    apiCalls: number;
    emailCampaigns: number;
  };
  recommended?: boolean;
  currentPlan?: boolean;
  activeTab?: 'features' | 'limits'; // Track active tab for each plan
}



interface SubscriptionReminder {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  action_label: string;
  action_url: string;
}

@Component({
  selector: 'app-subscription-billing',
  templateUrl: './subscription-billing.component.html',
  styleUrls: ['./subscription-billing.component.scss']
})
export class SubscriptionBillingComponent implements OnInit {
  
  // Loading state
  isLoading = false;
  isLoadingAddons = false;
  
  // Subscription state
  hasActiveSubscription = false;
  
  // Current Package Info
  currentPackage: any;
  /** Selected new package awaiting payment (plan change not yet completed). */
  pendingPackage: any = null;
  hasPendingPlanChange = false;
  /** Plan change scheduled for next billing cycle (invoice not sent yet). */
  scheduledPackage: any = null;
  hasScheduledPlanChange = false;

  // Reminders
  reminders: SubscriptionReminder[] = [];

  // Modal state
  showUpgradeModal = false;
  showRenewModal = false;
  showPurchaseModal = false;
  showAddonsModal = false;
  showPlanChangeConfirmModal = false;

  /** When the new package should take effect after upgrade/downgrade. */
  planChangeTiming: 'immediate' | 'next_cycle' = 'immediate';
  pendingPlanChange: SubscriptionPlan | null = null;
  
  // Payment Modal State
  showPaymentModal = false;
  paymentDetails: any = null;
  selectedAddon: Addon | null = null;
  isPaymentLoading = false;
  currentInvoice: any = null;
  // Track the package name we expect to pay for (handles cases where invoice still has old name)
  currentPaymentPackageName: string | null = null;

  selectedPlan: SubscriptionPlan | null = null;
  selectedBillingCycle: 'monthly' | 'yearly' = 'monthly';

  // Available subscription plans
  subscriptionPlans: SubscriptionPlan[] = [];


  // Usage Metrics
  usageMetrics: UsageMetric[] = [];

  // Invoices
  invoices: Invoice[] = [];
  invoiceFilterArray = [
    { key: 'invoice_no', value: 'Invoice number' },
    { key: 'invoice_type', value: 'Invoice type' },
    { key: 'status', value: 'Status' },
    { key: 'billing_context', value: 'Description' },
    { key: 'amount', value: 'Amount' },
  ];
  invoiceFilterType = '';
  invoiceSearchInput = '';
  invoiceSearchAction: 'search' | 'reset' = 'search';
  invoiceSearchError = '';
  invoiceStatusOptions = ['Paid', 'Pending', 'Overdue', 'Expired', 'Draft'];
  invoiceTypeOptions = ['TAX', 'PROFORMA', 'CREDIT'];
  invoiceTotalRows = 0;
  invoicePageIndex = 0;
  invoicePageSize = 10;
  invoicePageSizeOptions = [10, 25, 50];
  isExportingInvoices = false;
  invoiceSearchActive = false;

  // Available Addons (will be fetched from API)
  addons: Addon[] = [];
  
  // Active Plan Features
  activeFixedFeatures: any[] = [];
  
  // Active Addons
  activeAddons: any[] = [];

  activeTab: 'subscription' | 'invoices' | 'addons' = 'subscription';

  private readonly lastPaidPackageStorageKey = 'billing_last_paid_package';
  private readonly pendingPlanSelectionKey = 'billing_pending_plan_selection';

  constructor( 
    private subscriptionService: SubscriptionService,
    private localStorageService: LocalStorageService,
    private toastr: ToastrService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private usersService: UsersService,
    private permissionService: PermissionService,
    private subscriptionBillingLockService: SubscriptionBillingLockService
  ) { }

  showPaymentFailedModal = false;
  isCancelingPendingPlanChange = false;
  isSchedulingPlanChange = false;

  ngOnInit(): void {
    // Check if redirected back from a CCAvenue payment result.
    this.activatedRoute.queryParams.subscribe(params => {
      console.log('[Billing] Query params on load:', params);

      if (params['payment'] === 'success_payment') {
        console.log('[Billing] ✅ Payment success redirect detected — refreshing role & permissions...');
        this.toastr.success('Payment successful! Refreshing your account features...');
        this.clearLastPaidPackageSnapshot();
        this.clearPendingPlanSelection();
        this.refreshRoleAndPermissions();
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {},
          replaceUrl: true
        });
      } else if (params['payment'] === 'failed_payment') {
        console.log('[Billing] ❌ Payment failed redirect detected.');
        this.showPaymentFailedModal = true;
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {},
          replaceUrl: true
        });
      } else if (
        params['tab'] === 'subscription' ||
        params['tab'] === 'invoices' ||
        params['tab'] === 'addons'
      ) {
        this.setActiveTab(params['tab']);
      }
    });

    this.fnActiveSubscriptionData();
  }

  closePaymentFailedModal(): void {
    this.showPaymentFailedModal = false;
  }

  isPlansLoading: boolean = false;

  loadSubscriptionPlan() {
    this.isPlansLoading = true;
    const data = {
        "where": {
          "status": "Active",
          "visibility": "PUBLIC"
        },
        "filter": null,
        "page": 0,
        "limit": 25,
        "order": {
          "col": "sort_order",
          "dir": "ASC"
        }
    }
    this.subscriptionService.getAllPackage(data).subscribe({
      next: (res) => {
        console.log('Package API Response:', res);
        
        let packages: any[] = [];
        
        if (res.data && res.data.rows && Array.isArray(res.data.rows)) {
          packages = res.data.rows;
        } else if (res.data && Array.isArray(res.data)) {
          packages = res.data;
        } else if (res.rows && Array.isArray(res.rows)) {
          packages = res.rows;
        } else if (Array.isArray(res)) {
          packages = res;
        }
        
         

        if (packages.length === 0) {
          console.warn('No packages found in API response');
          this.isPlansLoading = false;
          return;
        }
        
        this.subscriptionPlans = packages.map((pkg: any, index: number) => {
          let features: string[] = [];
          let fixedFeatures: string[] = [];
          let limitsFeatures: string[] = [];
          const limits = { users: -1, storage: -1, apiCalls: -1, emailCampaigns: -1 };
          
          if (pkg.features && typeof pkg.features === 'object' && !Array.isArray(pkg.features)) {
            if (pkg.features.fixed && Array.isArray(pkg.features.fixed)) {
              fixedFeatures = pkg.features.fixed
                .filter((f: any) => f.enabled === true)
                .map((f: any) => f.label || this.normaliseText(f.key || 'Feature'));
              features.push(...fixedFeatures);
            }
            if (pkg.features.limits && Array.isArray(pkg.features.limits)) {
              limitsFeatures = pkg.features.limits
                .filter((l: any) => l.enabled === true)
                .map((l: any) => {
                  const value = l.value === -1 ? 'Unlimited' : this.formatNumber(l.value);
                  const label = l.label || this.normaliseText(l.key || 'Limit');
                  const unit = l.unit ? ` ${l.unit}` : '';

                  const key = (l.key || '').toLowerCase();
                  if (key.includes('user') || key.includes('member')) limits.users = l.value;
                  if (key.includes('storage')) limits.storage = l.value;
                  if (key.includes('api')) limits.apiCalls = l.value;
                  if (key.includes('email') || key.includes('campaign')) limits.emailCampaigns = l.value;

                  return `${value} ${label}${unit}`;
                });
              features.push(...limitsFeatures);
            }
          } else if (Array.isArray(pkg.features)) {
            features = pkg.features.map((f: string) => this.normaliseText(f));
            fixedFeatures = [...features];
          } else if (pkg.features && typeof pkg.features === 'object') {
            features = Object.entries(pkg.features).map(([key, value]) => {
              const displayValue = value === -1 ? 'Unlimited' : this.formatNumber(value as number);
              return `${displayValue} ${this.normaliseText(key)}`;
            });
            limitsFeatures = [...features];
          }

         
          const price = parseFloat(pkg.price) || 0;
          const billingCycle = pkg.billing_cycle?.toLowerCase() || 'monthly';
          let monthlyPrice = price;
          let yearlyPrice = price;
          
          if (billingCycle === 'monthly') {
            monthlyPrice = price;
            yearlyPrice = price * 12 * 0.85;
          } else if (billingCycle === 'yearly') {
            yearlyPrice = price;
            monthlyPrice = price / 12;
          }

          return {
            id: pkg.package_id,
            name: pkg.package_name,
            description: pkg.description,
            monthlyPrice: monthlyPrice,
            yearlyPrice: yearlyPrice,
            features: features.slice(0, 15),
            fixedFeatures: fixedFeatures,
            limitsFeatures: limitsFeatures,
            limits: limits,
            recommended: pkg.is_recommended || false,
            isTrial: pkg.is_trial || false,
            trialPeriod: pkg.trial_period || 0,
            visibility: pkg.visibility || 'PUBLIC',
            currentPlan: false,
            activeTab: 'features'
          };
        });

        // If client is already on Trial, don't offer Trial again in plan selection modals
        if (this.currentPackage?.is_trial) {
          this.subscriptionPlans = this.subscriptionPlans.filter((p) => !p.isTrial);
        }

        this.markCurrentPlanInList();
        this.isPlansLoading = false;
      },
      error: (err) => {
        console.error('Error loading packages:', err);
        this.isPlansLoading = false;
      }
    })
  }

  fnGetAllInvoices(resetPage = true): void {
    this.loadInvoicesFromApi(resetPage);
  }

  private buildInvoiceRequestPayload(options?: { exportAll?: boolean }): Record<string, unknown> {
    const payload: Record<string, unknown> = {
      client_id: this.localStorageService.getClientId(),
      page: this.invoicePageIndex,
      limit: this.invoicePageSize,
      order: {
        col: 'createdAt',
        dir: 'DESC',
      },
    };

    if (this.currentPackage?.package_subscription_id) {
      payload['package_subscription_id'] = this.currentPackage.package_subscription_id;
    }

    if (options?.exportAll) {
      payload['exportAll'] = true;
    }

    if (this.invoiceSearchActive && this.invoiceFilterType && this.invoiceSearchInput.trim()) {
      payload['filter'] = {
        [this.invoiceFilterType]: this.invoiceSearchInput.trim(),
      };
    }

    return payload;
  }

  private loadInvoicesFromApi(resetPage = false): void {
    if (!this.localStorageService.getClientId()) {
      this.invoices = [];
      this.invoiceTotalRows = 0;
      return;
    }

    if (resetPage) {
      this.invoicePageIndex = 0;
    }

    this.isLoading = true;

    this.subscriptionService.getAllInvoices(this.buildInvoiceRequestPayload()).subscribe({
      next: (res) => {
        this.invoices = Array.isArray(res?.rows) ? res.rows : [];
        this.invoiceTotalRows = Number(res?.count) || 0;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading invoices:', err);
        this.invoices = [];
        this.invoiceTotalRows = 0;
        this.isLoading = false;
      },
    });
  }

  onInvoiceFilterTypeChange(): void {
    this.invoiceSearchInput = '';
    this.invoiceSearchError = '';
    this.invoiceSearchActive = false;
    this.invoiceSearchAction = 'search';
    this.loadInvoicesFromApi(true);
  }

  clearInvoiceSearchError(): void {
    this.invoiceSearchError = '';
  }

  applyInvoiceSearch(): void {
    this.invoiceSearchError = '';

    if (!this.invoiceFilterType) {
      this.invoiceSearchError = 'Please select a filter type';
      return;
    }

    const query = (this.invoiceSearchInput || '').trim();
    if (!query) {
      this.invoiceSearchError = 'Please enter a value to search';
      return;
    }

    this.invoiceSearchActive = true;
    this.invoiceSearchAction = 'reset';
    this.loadInvoicesFromApi(true);
  }

  onInvoiceDropdownSearch(value: string, field: 'status' | 'invoice_type'): void {
    this.invoiceFilterType = field;
    this.invoiceSearchInput = value;
    this.invoiceSearchError = '';
    this.invoiceSearchActive = !!value;
    this.invoiceSearchAction = value ? 'reset' : 'search';
    this.loadInvoicesFromApi(true);
  }

  resetInvoiceSearch(): void {
    this.invoiceFilterType = '';
    this.invoiceSearchInput = '';
    this.invoiceSearchError = '';
    this.invoiceSearchAction = 'search';
    this.invoiceSearchActive = false;
    this.loadInvoicesFromApi(true);
  }

  onInvoicePageChange(event: PageEvent): void {
    this.invoicePageIndex = event.pageIndex;
    this.invoicePageSize = event.pageSize;
    this.loadInvoicesFromApi(false);
  }

  exportAllInvoices(): void {
    if (this.invoiceTotalRows === 0 && !this.invoiceSearchActive) {
      this.toastr.error('No invoices available to export');
      return;
    }

    this.isExportingInvoices = true;

    this.subscriptionService.getAllInvoices(this.buildInvoiceRequestPayload({ exportAll: true })).subscribe({
      next: (res) => {
        const exportRows = Array.isArray(res?.rows) ? res.rows : [];

        if (!exportRows.length) {
          this.toastr.error('No invoices available to export');
          this.isExportingInvoices = false;
          return;
        }

        try {
          const sheetData = exportRows.map((invoice: Invoice) => ({
            'Invoice number': invoice.invoice_no,
            'Invoice type': invoice.invoice_type,
            'Paid date': invoice.paid_at ? this.formatInvoiceExportDate(invoice.paid_at) : 'N/A',
            'Description': invoice.billing_context || '',
            'Amount': invoice.invoice_type === 'PROFORMA' ? invoice.amount : invoice.total_amount,
            'Due date': invoice.payment_link_expires_at
              ? this.formatInvoiceExportDate(invoice.payment_link_expires_at)
              : 'N/A',
            'Status': invoice.status,
          }));

          const worksheet = XLSX.utils.json_to_sheet(sheetData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice History');
          const fileName = `invoice-history-${new Date().toISOString().slice(0, 10)}.xlsx`;
          XLSX.writeFile(workbook, fileName);
          this.toastr.success('Invoice history exported successfully');
        } catch (error) {
          console.error('Invoice export failed:', error);
          this.toastr.error('Failed to export invoice history');
        } finally {
          this.isExportingInvoices = false;
        }
      },
      error: (err) => {
        console.error('Invoice export failed:', err);
        this.toastr.error('Failed to export invoice history');
        this.isExportingInvoices = false;
      },
    });
  }

  private formatInvoiceExportDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  getInvoiceEmptyMessage(): string {
    if (this.invoiceSearchActive) {
      return 'No invoices match your search criteria';
    }
    return 'Your invoice history will appear here';
  }

  handleReminderAction(reminder: SubscriptionReminder): void {
    console.log('Handling reminder action:', reminder);
    
    // Check if we need to open a modal based on the type/action
    const type = reminder.type;
    
    // Logic to open specific modals based on reminder type
    if (type === 'TRIAL_ACTIVE' || type === 'TRIAL_EXPIRING' || type === 'TRIAL_EXPIRED') {
      this.openPurchaseModal(); // Or openUpgradeModal based on logic
      return;
    }
    
    // Only open renew modal when subscription has actually expired, not just expiring soon
    if (type === 'SUBSCRIPTION_EXPIRED' || type === 'GRACE_PERIOD') {
      if (this.currentPackage?.status === 'Inactive' && this.isSubscriptionExpired()) {
        this.openRenewModal();
        return;
      }
    }

    if (type === 'PAYMENT_ISSUE') {
       // If pending payment
       if (this.currentPackage?.package_subscription_id) {
         this.completePackagePurchase(this.currentPackage.package_subscription_id);
         return;
       }
    }

    if (type === 'PENDING_PLAN_CHANGE') {
      if (this.hasPendingPlanChange) {
        this.completePendingPlanPayment();
        return;
      }
    }

    // Fallback to navigation if provided
    if (reminder.action_url) {
      this.router.navigate([reminder.action_url]);
    }
  }

  fnActiveSubscriptionData(): void {
    this.isLoading = true;
    this.usageMetrics = [];
    this.activeFixedFeatures = [];
    this.activeAddons = [];
    const data = { client_id:this.localStorageService.getClientId() }
    this.subscriptionService.getCurrentUserActivePackage(data, { forceRefresh: true }).subscribe({
      next: (res) => {
        if (!res.data?.id) {
          this.hasActiveSubscription = false;
          this.reminders = res.data?.reminders || [];
          this.isLoading = false;
          return;
        }

        this.hasActiveSubscription = true;
        let subscriptionData = res.data;
        console.log('Active Subscription Data:', subscriptionData);
        let packageData = res.data.package || {};
        this.currentInvoice = res.data.invoices[0];
        this.hasPendingPlanChange = !!subscriptionData.has_pending_plan_change;
        this.pendingPackage = null;
        this.hasScheduledPlanChange = !!subscriptionData.has_scheduled_plan_change;
        this.scheduledPackage = null;

        const fallback = this.applyLastPaidPackageFallback(subscriptionData, packageData);
        if (fallback && !subscriptionData.has_scheduled_plan_change) {
          subscriptionData = fallback.subscriptionData;
          packageData = fallback.packageData;
          this.hasPendingPlanChange = true;
        }

        const pendingSelection = this.applyPendingPlanSelectionFallback(
          subscriptionData,
          packageData,
        );
        if (pendingSelection && !subscriptionData.has_scheduled_plan_change) {
          subscriptionData = pendingSelection.subscriptionData;
          packageData = pendingSelection.packageData;
          this.hasPendingPlanChange = true;
        }

        if (this.hasPendingPlanChange && subscriptionData.pending_package) {
          const pending = subscriptionData.pending_package;
          this.pendingPackage = {
            package_subscription_id: subscriptionData.id,
            package_id: pending.package_id,
            name: pending.package_name,
            price: pending.price,
            billingCycle: pending.billing_cycle,
            currency: pending.currency,
            payment_status: subscriptionData.payment_status,
          };
        }

        if (this.hasScheduledPlanChange && subscriptionData.scheduled_package) {
          const scheduled = subscriptionData.scheduled_package;
          this.scheduledPackage = {
            package_subscription_id: subscriptionData.id,
            package_id: scheduled.package_id,
            name: scheduled.package_name,
            price: scheduled.price,
            billingCycle: scheduled.billing_cycle,
            currency: scheduled.currency,
            effectiveAt: subscriptionData.scheduled_effective_at,
          };
        }

        const rawReminders: SubscriptionReminder[] = res.data.reminders || [];
        this.reminders = rawReminders.filter(
          (r) => r.severity === 'warning' || r.severity === 'critical'
        );
        this.currentPackage  = {
          subscription_id: subscriptionData.subscription_id,
          package_subscription_id: subscriptionData.id,
          package_id: packageData.package_id,
          name: packageData.package_name,
          price: packageData.price,
          billingCycle: packageData.billing_cycle,
          currency: packageData.currency,
          nextBillingDate: subscriptionData.next_billing_at,
          status: subscriptionData.display_status || subscriptionData.status,
          payment_status:
            subscriptionData.display_payment_status || subscriptionData.payment_status,
          raw_status: subscriptionData.status,
          raw_payment_status: subscriptionData.payment_status,
          is_trial: subscriptionData.is_trial || packageData.is_trial,
          trial_ends_at: subscriptionData.trial_ends_at,
          fixedFeatures: [],
          limitsFeatures: [],
        };

        (subscriptionData.usage || []).forEach((metric: any) => {
          if(metric?.feature_type === 'LIMIT'){
              this.usageMetrics.push({
                name: metric?.feature_label,
                key: metric?.feature_key,
                type: metric?.feature_type,
                current: metric?.used_count,
                limit: metric?.allowed_limit,
                unit:  metric?.unit_type || 'Count',
                icon: 'bi-people-fill',
                color: '#0d4259'
              });
        }
        if(metric?.feature_type === 'FIXED'){
          this.activeFixedFeatures.push({
            name: metric?.feature_label,
            key: metric?.feature_key,
            type: metric?.feature_type,
            current: metric?.used_count,
            limit: metric?.allowed_limit,
            unit:  metric?.unit_type || 'Count',
            icon: 'bi-people-fill',
            color: '#0d4259'
          });
          this.currentPackage.fixedFeatures.push(metric?.feature_label);
        }
         
        });

        this.currentPackage.limitsFeatures = this.usageMetrics.map((metric) => {
          const limitLabel =
            metric.limit === -1 ? 'Unlimited' : this.formatNumber(metric.limit);
          return `${metric.name}: ${limitLabel} ${this.getUnitLableByUnit(metric.unit)}`.trim();
        });

        this.markCurrentPlanInList();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load active subscription:', err);
        this.hasActiveSubscription = false;
        this.isLoading = false;
      }
    })
  }

  getDataByFeatureKey(featureKey: string,data: any): string {
    return data.find((feature: any) => feature.key === featureKey);
  }
  getUsagePercentage(metric: UsageMetric): number {
    return (metric.current / metric.limit) * 100;
  }

  getProgressBarClass(percentage: number): string {
    if (percentage >= 90) return 'danger';
    if (percentage >= 75) return 'warning';
    return 'success';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'paid': return 'badge-success';
      case 'pending': return 'badge-warning';
      case 'overdue': return 'badge-danger';
      default: return 'badge-secondary';
    }
  }

  getQrImage(qr: string): string {
  const raw = qr.replace(/^data:image\/\w+;base64,/, '');
  const fixed = this.convertBase64UrlToBase64(raw);
  return `data:image/png;base64,${fixed}`;
}

convertBase64UrlToBase64(base64Url: string): string {
  if (!base64Url) return '';

  let base64 = base64Url
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  // Pad with '=' if needed
  const pad = base64.length % 4;
  if (pad) {
    base64 += '='.repeat(4 - pad);
  }

  return base64;
}

  downloadInvoice(invoice: Invoice): void {
    if (!invoice.invoice_url) {
      alert('Invoice PDF is not available for download');
      return;
    }
    const link = document.createElement('a');
    link.href = invoice.invoice_url;
    link.download = `Invoice_${invoice.invoice_no}.pdf`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  viewInvoice(invoice: Invoice): void {
    if (!invoice.invoice_url) {
      alert('Invoice PDF is not available for viewing');
      return;
    }
    window.open(invoice.invoice_url, '_blank');
  }

  purchaseAddon(addon: Addon): void {
    console.log('Purchasing addon:', addon.name);
    this.selectedAddon = addon;
    this.showPaymentModal = true;
    this.isPaymentLoading = true;
    this.paymentDetails = null;
    this.currentPaymentPackageName = null;
    
    this.fnPurchaseAddon(addon);
  }

  completePackagePurchase(
    packageSubscriptionId: string,
    options?: {
      forcePlanChange?: boolean;
      packageId?: string | number;
      billingCycle?: string;
      planChangeTiming?: 'immediate' | 'next_cycle';
      periodStart?: string;
    },
  ): void {
    console.log('Completing payment for package:', packageSubscriptionId);
    this.selectedAddon = null;
    this.showPaymentModal = true;
    this.isPaymentLoading = true;
    this.paymentDetails = null;
    // When paying for a package subscription directly:
    // - If an explicit name was set earlier (e.g. from upgrade/purchase flow), keep it.
    // - Otherwise, fall back to the current package name from subscription data.
    this.currentPaymentPackageName = this.currentPaymentPackageName || this.currentPackage?.name || null;

    const payload: any = {
      package_subscription_id: packageSubscriptionId,
      client_id: this.localStorageService.getClientId(),
    };

    if (options?.forcePlanChange) {
      payload.force_plan_change = true;
    }
    if (options?.packageId != null) {
      payload.package_id = options.packageId;
    }
    if (options?.billingCycle) {
      payload.billing_cycle = options.billingCycle;
    }
    if (options?.planChangeTiming) {
      payload.plan_change_timing = options.planChangeTiming;
    }
    if (options?.periodStart) {
      payload.current_period_start = options.periodStart;
    }

    this.subscriptionService.proformaPackageInvoice(payload).subscribe({
      next: (res: any) => {
        console.log("Package payment details:", res);
        // Handle skipped responses (e.g. "not yet due for billing")
        if (res?.skipped) {
          this.toastr.error(res.message || 'Unable to generate invoice at this time.');
          this.closePaymentModal();
          return;
        }
        if (res && (res.payment_link || res.reused || res.paid)) {
          this.paymentDetails = res;
          this.isPaymentLoading = false;
        } else {
          this.toastr.error(res?.message || 'Failed to generate payment link');
          this.closePaymentModal();
        }
      },
      error: (err: any) => {
        console.error('Package payment generation error:', err);
        const msg = err?.error?.message || err?.message || 'Failed to generate payment link';
        this.toastr.error(msg);
        this.closePaymentModal();
      }
    });
  }

  completeAddonPayment(addon: any): void {
    console.log('Completing payment for addon:', addon);
    this.selectedAddon = addon;
    this.showPaymentModal = true;
    this.isPaymentLoading = true;
    this.paymentDetails = null;
    
    // Call the same payment API with the subscription_id
    const payload = {
      addon_subscription_id: addon.id,
      client_id: this.localStorageService.getClientId()
    };
    
    this.subscriptionService.proformaAddonInvoice(payload).subscribe({
      next: (res: any) => {
      //  console.log("paymentDetails", res);
        if (res) {
          this.paymentDetails = res;
          console.log("paymentDetails", this.paymentDetails);
          this.isPaymentLoading = false;
        } else {
          this.toastr.error(res.message || 'Failed to generate payment link');
          this.closePaymentModal();
        }
      },
      error: (err: any) => {
        console.error('Payment generation error:', err);
        this.toastr.error(err.message || 'Failed to generate payment link');
        this.closePaymentModal();
      }
    });
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
    this.selectedAddon = null;
    this.paymentDetails = null;
    this.isPaymentLoading = false;
    this.currentPaymentPackageName = null;
  }

  openAddonsModal(): void {
    this.showAddonsModal = true;
    this.fnGetAddonPackages();
  }

  closeAddonsModal(): void {
    this.showAddonsModal = false;
  }

  increaseQuantity(addon: any): void {
    if (!addon.quantity) {
      addon.quantity = 1;
    }
    if (addon.quantity < 99) {
      addon.quantity++;
    }
  }

  decreaseQuantity(addon: any): void {
    if (!addon.quantity) {
      addon.quantity = 1;
    }
    if (addon.quantity > 1) {
      addon.quantity--;
    }
  }

  loadingAddonId: string | null = null;

  addAddonToSubscription(addon: any): void {
    console.log('Adding addon to subscription:', this.currentPackage);
    
    // Get package_subscription_id from currentPackage (loaded by fnActiveSubscriptionData)
    const packageSubscriptionId = this.currentPackage?.package_subscription_id;
    
    if (!packageSubscriptionId) {
      this.toastr.error('No active subscription found. Please subscribe to a package first.');
      return;
    }

    this.loadingAddonId = addon.id;

    const payload = {
      client_id: this.localStorageService.getClientId(), // TODO: Get from actual user session
      subscription_id: this.currentPackage?.subscription_id,
      package_subscription_id: packageSubscriptionId,
      addon_id: addon.id,
      quantity: addon.quantity || 1,
      billing_type: 'Fixed' // Always fixed
    };

    console.log('Creating addon subscription with payload:', payload);

    this.subscriptionService.createAddonSubscription(payload).subscribe({
      next: (res: any) => {
        console.log('Addon subscription created:', res);
        if (res) {
          this.toastr.success(res.message || 'Add-on added successfully!');
          this.closeAddonsModal();
          // Reload active addons
          this.fnLoadAllAddonSubscription();
        } else {
          this.toastr.error(res.message || 'Failed to add add-on');
        }
        this.loadingAddonId = null;
      },
      error: (err: any) => {
        console.error('Error creating addon subscription:', err);
        this.toastr.error(err.message || 'Failed to add add-on');
        this.loadingAddonId = null;
      }
    });
  }

  setActiveTab(tab: 'subscription' | 'invoices' | 'addons'): void {
    this.activeTab = tab;
    if (tab === 'subscription') {
      this.fnActiveSubscriptionData();
    } else if (tab === 'invoices') {
      this.fnGetAllInvoices();
    } else if (tab === 'addons') {
      this.fnLoadAllAddonSubscription();
    }
  }

  normaliseText(text: string): string {
    return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
  }

  formatCurrency(amount: number | string): string {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currentPackage?.currency || "INR"
    }).format(numAmount);
  }

  fnGetAddonPackages(){
    this.isLoading = true;
    const data = {
        "page": 0,
        "limit": 1000,
        "where": {
          "is_active": true
        },
        "order": {
          "col": "name",
          "dir": "ASC"
        }
  }
    this.subscriptionService.getAllPackageAddon(data).subscribe({
      next: (res) => {
        console.log('Addon packages response:', res);
        if (res && res.rows) {
          this.addons = res.rows.map((addon: any) => ({
            ...addon,
            icon: this.getAddonIcon(addon),
            popular: false,
            activeTab: 'credits',
            quantity: 1 // Initialize quantity
          }));
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading addon packages:', err);
        this.isLoading = false;
      }
    })
  }
  // Helper method to determine icon based on addon features
  getAddonIcon(addon: any): string {
    if (!addon.feature || !addon.feature.limits || addon.feature.limits.length === 0) {
      return 'bi-puzzle-fill';
    }
    
    const firstLimit = addon.feature.limits[0];
    const key = firstLimit.key.toLowerCase();
    
    // Map feature keys to appropriate icons
    if (key.includes('storage')) return 'bi-hdd-fill';
    if (key.includes('user') || key.includes('member')) return 'bi-people-fill';
    if (key.includes('email')) return 'bi-envelope-fill';
    if (key.includes('sms') || key.includes('message')) return 'bi-chat-dots-fill';
    if (key.includes('api')) return 'bi-code-square';
    if (key.includes('contact')) return 'bi-person-lines-fill';
    if (key.includes('campaign')) return 'bi-megaphone-fill';
    if (key.includes('template')) return 'bi-file-earmark-text-fill';
    
    return 'bi-puzzle-fill'; // Default icon
  }

  formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  }

  // Upgrade/Renew Plan Methods
  openUpgradeModal(): void {

    this.showUpgradeModal = true;
    this.selectedPlan = null;
    this.selectedBillingCycle = 'monthly';
    this.saveLastPaidPackageSnapshot();

    this.loadSubscriptionPlan();
  }

  closeUpgradeModal(): void {
    this.showUpgradeModal = false;
    this.selectedPlan = null;
  }

  isRenewLoading = false;

  /**
   * Returns true only when the subscription billing/expiry date has already passed.
   * This ensures "Renew Plan" is shown only for truly expired subscriptions,
   * not for ones that are still active but expiring soon.
   */
  isSubscriptionExpired(): boolean {
    const expiryDate = this.currentPackage?.nextBillingDate;
    if (!expiryDate) return true; // no date stored → treat as expired
    const expiry = new Date(expiryDate);
    expiry.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today >= expiry; // true on expiry date and any day after
  }

  /** Expired/inactive subscription — user can renew the same plan. */
  showExpiredRenewOption(): boolean {
    return (
      this.currentPackage?.status === 'Inactive' &&
      this.isSubscriptionExpired() &&
      !this.hasPendingPlanChange &&
      !this.hasScheduledPlanChange &&
      !this.currentPackage?.is_trial
    );
  }

  /** Expired subscription — user can pick a different plan instead of renewing. */
  showExpiredChangePlanOption(): boolean {
    return this.showExpiredRenewOption();
  }

  openExpiredChangePlanModal(): void {
    this.openUpgradeModal();
  }

  openRenewModal(): void {
    this.showRenewModal = true;
    this.isRenewLoading = false;
  }

  closeRenewModal(): void {
    this.showRenewModal = false;
    this.isRenewLoading = false;
  }

  selectPlan(plan: SubscriptionPlan): void {
    this.selectedPlan = plan;
  }

  toggleBillingCycle(cycle: 'monthly' | 'yearly'): void {
    this.selectedBillingCycle = cycle;
  }

  getSelectedPlanPrice(): number {
    if (!this.selectedPlan) return 0;
    return this.selectedBillingCycle === 'monthly' 
      ? this.selectedPlan.monthlyPrice 
      : this.selectedPlan.yearlyPrice;
  }

  getSavingsPercentage(): number {
    if (!this.selectedPlan || this.selectedPlan.isTrial) return 0;
    const monthlyTotal = this.selectedPlan.monthlyPrice * 12;
    const yearlyTotal = this.selectedPlan.yearlyPrice;
    return Math.round(((monthlyTotal - yearlyTotal) / monthlyTotal) * 100);
  }

  getYearlySavingsAmount(): number {
    if (!this.selectedPlan || this.selectedPlan.isTrial) return 0;
    const monthlyTotal = this.selectedPlan.monthlyPrice * 12;
    const yearlyTotal = this.selectedPlan.yearlyPrice;
    return monthlyTotal - yearlyTotal;
  }

  confirmUpgrade(): void {
    if (!this.selectedPlan) return;
    
    console.log('Upgrading to:', {
      plan: this.selectedPlan.name,
      billingCycle: this.selectedBillingCycle,
      price: this.getSelectedPlanPrice()
    });

    // TODO: Implement API call to upgrade plan
    // this.http.post(environment.upgradePlan, {
    //   planId: this.selectedPlan.id,
    //   billingCycle: this.selectedBillingCycle
    // }).subscribe(...)

    // For now, just close the modal and show success message
    alert(`Successfully upgraded to ${this.selectedPlan.name} (${this.selectedBillingCycle})!`);
    this.closeUpgradeModal();
  }

  confirmRenewal(): void {
    if (!this.currentPackage?.package_subscription_id) {
      this.toastr.error('No active subscription found to renew.');
      return;
    }

    this.isRenewLoading = true;
    // Track the package name for the payment modal header
    this.currentPaymentPackageName = this.currentPackage.name;

    // Close the renew confirmation modal and open the proforma payment modal
    this.closeRenewModal();
    this.completePackagePurchase(this.currentPackage.package_subscription_id);
  }

  getAvailableUpgradePlans(): SubscriptionPlan[] {
    return this.getSelectablePlans();
  }

  /** All public plans for upgrade/downgrade, with the active plan flagged. */
  getSelectablePlans(): SubscriptionPlan[] {
    if (!this.subscriptionPlans.length) {
      return [];
    }

    const currentPackageId = this.currentPackage?.package_id;
    const isCurrentlyOnTrial = !!this.currentPackage?.is_trial;

    return this.subscriptionPlans
      .filter((plan) => !(isCurrentlyOnTrial && plan.isTrial))
      .map((plan) => ({
        ...plan,
        currentPlan: String(plan.id) === String(currentPackageId),
      }));
  }

  private markCurrentPlanInList(): void {
    const currentPackageId = this.currentPackage?.package_id;
    if (!currentPackageId) {
      return;
    }

    this.subscriptionPlans = this.subscriptionPlans.map((plan) => ({
      ...plan,
      currentPlan: String(plan.id) === String(currentPackageId),
    }));
  }

  isPlanCurrent(plan: SubscriptionPlan): boolean {
    return !!plan.currentPlan;
  }

  getPlanChangeLabel(plan?: SubscriptionPlan | null): string {
    const target = plan || this.selectedPlan;
    if (!target) {
      return 'Confirm Purchase';
    }
    if (this.isPlanUpgrade(target)) {
      return 'Confirm Upgrade';
    }
    if (this.isPlanDowngrade(target)) {
      return 'Confirm Downgrade';
    }
    return 'Confirm Purchase';
  }

  getPlanChangeActionLabel(plan?: SubscriptionPlan | null): string {
    const target = plan || this.selectedPlan;
    if (!target) {
      return 'Change Plan';
    }
    if (this.isPlanUpgrade(target)) {
      return 'Upgrade';
    }
    if (this.isPlanDowngrade(target)) {
      return 'Downgrade';
    }
    return 'Switch Plan';
  }

  openPlanChangeConfirmation(): void {
    if (!this.selectedPlan || this.isPlanCurrent(this.selectedPlan)) {
      return;
    }

    this.pendingPlanChange = this.selectedPlan;
    this.planChangeTiming = 'immediate';
    this.showPlanChangeConfirmModal = true;
  }

  closePlanChangeConfirmModal(): void {
    this.showPlanChangeConfirmModal = false;
    this.pendingPlanChange = null;
  }

  confirmPlanChangeAndPay(): void {
    if (!this.pendingPlanChange) {
      return;
    }

    const plan = this.pendingPlanChange;
    const timing = this.planChangeTiming;

    this.closePlanChangeConfirmModal();
    this.closeUpgradeModal();

    if (timing === 'next_cycle') {
      this.schedulePlanChangeForNextCycle(plan);
      return;
    }

    // Immediate: open payment modal and generate proforma now.
    this.selectedAddon = null;
    this.showPaymentModal = true;
    this.isPaymentLoading = true;
    this.paymentDetails = null;
    this.currentPaymentPackageName = plan.name;
    this.executePlanChange(plan, timing);
  }

  private schedulePlanChangeForNextCycle(plan: SubscriptionPlan): void {
    if (!this.currentPackage?.package_subscription_id) {
      this.toastr.error('No active subscription found to update.');
      return;
    }

    if (!this.currentPackage?.nextBillingDate) {
      this.toastr.error('Next billing date is not available for this subscription.');
      return;
    }

    const clientId = this.localStorageService.getClientId();
    this.isSchedulingPlanChange = true;

    this.subscriptionService
      .schedulePlanChange({
        package_subscription_id: this.currentPackage.package_subscription_id,
        client_id: clientId,
        package_id: plan.id,
        billing_cycle: this.selectedBillingCycle,
        effective_at: new Date(this.currentPackage.nextBillingDate).toISOString(),
      })
      .subscribe({
        next: (res: any) => {
          if (res?.success === false) {
            this.toastr.error(
              res?.message ||
                'Failed to schedule plan change for next billing cycle.',
            );
            this.isSchedulingPlanChange = false;
            return;
          }

          this.clearPendingPlanSelection();
          this.clearLastPaidPackageSnapshot();
          if (clientId) {
            clearActiveUserPackageCache(clientId);
          }
          this.toastr.success(
            res?.message ||
              `Plan change scheduled for ${this.formatScheduleDate(this.currentPackage.nextBillingDate)}.`,
          );
          this.fnActiveSubscriptionData();
          this.isSchedulingPlanChange = false;
        },
        error: (err: unknown) => {
          console.error('Error scheduling plan change:', err);
          this.toastr.error(
            this.getApiErrorMessage(
              err,
              'Failed to schedule plan change for next billing cycle.',
            ),
          );
          this.isSchedulingPlanChange = false;
        },
      });
  }

  private formatScheduleDate(value: string | Date): string {
    return new Date(value).toLocaleDateString('en-GB');
  }

  private executePlanChange(
    plan: SubscriptionPlan,
    timing: 'immediate' | 'next_cycle',
  ): void {
    if (!this.currentPackage?.package_subscription_id) {
      this.toastr.error('No active subscription found to update.');
      return;
    }

    this.saveLastPaidPackageSnapshot();
    this.savePendingPlanSelection(plan, timing);
    this.currentPaymentPackageName = plan.name;

    const now = new Date();
    const periodStart = new Date(now);

    // Do not update the subscription record before payment — invoice uses the selected package.
    this.completePackagePurchase(this.currentPackage.package_subscription_id, {
      forcePlanChange: true,
      packageId: plan.id,
      billingCycle: this.selectedBillingCycle,
      planChangeTiming: timing,
      periodStart: periodStart.toISOString(),
    });
  }

  completePendingPlanPayment(): void {
    if (!this.pendingPackage?.package_subscription_id) {
      this.toastr.error('No pending package payment found.');
      return;
    }

    this.currentPaymentPackageName = this.pendingPackage.name;
    this.completePackagePurchase(this.pendingPackage.package_subscription_id, {
      forcePlanChange: true,
      packageId: this.pendingPackage.package_id,
      billingCycle: this.pendingPackage.billingCycle,
    });
  }

  openUpgradeModalWithPendingChange(): void {
    this.openUpgradeModal();
  }

  cancelPendingPlanChange(): void {
    const packageSubscriptionId =
      this.pendingPackage?.package_subscription_id ||
      this.currentPackage?.package_subscription_id;
    const clientId = this.localStorageService.getClientId();

    if (!packageSubscriptionId || !clientId) {
      this.toastr.error('Unable to cancel pending plan change.');
      return;
    }

    this.isCancelingPendingPlanChange = true;

    this.subscriptionService
      .cancelPendingPlanChange({
        package_subscription_id: packageSubscriptionId,
        client_id: clientId,
      })
      .subscribe({
        next: (res: any) => {
          this.clearPendingPlanSelection();
          this.clearLastPaidPackageSnapshot();
          clearActiveUserPackageCache(clientId);
          this.hasPendingPlanChange = false;
          this.pendingPackage = null;
          this.hasScheduledPlanChange = false;
          this.scheduledPackage = null;
          this.toastr.success(
            res?.message || 'Pending plan change cancelled. Your current package remains active.',
          );
          this.fnActiveSubscriptionData();
          this.isCancelingPendingPlanChange = false;
        },
        error: (err: unknown) => {
          console.error('Error cancelling pending plan change:', err);
          this.toastr.error(
            this.getApiErrorMessage(
              err,
              'Failed to cancel pending plan change.',
            ),
          );
          this.isCancelingPendingPlanChange = false;
        },
      });
  }

  private saveLastPaidPackageSnapshot(): void {
    const clientId = this.localStorageService.getClientId();
    if (!clientId || !this.currentPackage?.package_id) {
      return;
    }

    if (
      this.currentPackage.payment_status !== 'Paid' &&
      !this.hasPendingPlanChange
    ) {
      return;
    }

    const snapshot = {
      client_id: clientId,
      package_id: this.currentPackage.package_id,
      name: this.currentPackage.name,
      price: this.currentPackage.price,
      billingCycle: this.currentPackage.billingCycle,
      currency: this.currentPackage.currency,
      saved_at: Date.now(),
    };

    sessionStorage.setItem(
      this.lastPaidPackageStorageKey,
      JSON.stringify(snapshot),
    );
  }

  private loadLastPaidPackageSnapshot(): any | null {
    const clientId = this.localStorageService.getClientId();
    const raw = sessionStorage.getItem(this.lastPaidPackageStorageKey);
    if (!raw || !clientId) {
      return null;
    }

    try {
      const snapshot = JSON.parse(raw);
      if (String(snapshot.client_id) !== String(clientId)) {
        return null;
      }
      return snapshot;
    } catch {
      return null;
    }
  }

  private clearLastPaidPackageSnapshot(): void {
    sessionStorage.removeItem(this.lastPaidPackageStorageKey);
  }

  private savePendingPlanSelection(
    plan: SubscriptionPlan,
    timing: 'immediate' | 'next_cycle',
  ): void {
    const clientId = this.localStorageService.getClientId();
    if (!clientId) {
      return;
    }

    sessionStorage.setItem(
      this.pendingPlanSelectionKey,
      JSON.stringify({
        client_id: clientId,
        package_id: plan.id,
        name: plan.name,
        price:
          this.selectedBillingCycle === 'monthly'
            ? plan.monthlyPrice
            : plan.yearlyPrice,
        billingCycle: this.selectedBillingCycle,
        timing,
        saved_at: Date.now(),
      }),
    );
  }

  private loadPendingPlanSelection(): any | null {
    const clientId = this.localStorageService.getClientId();
    const raw = sessionStorage.getItem(this.pendingPlanSelectionKey);
    if (!raw || !clientId) {
      return null;
    }

    try {
      const selection = JSON.parse(raw);
      if (String(selection.client_id) !== String(clientId)) {
        return null;
      }
      return selection;
    } catch {
      return null;
    }
  }

  private clearPendingPlanSelection(): void {
    sessionStorage.removeItem(this.pendingPlanSelectionKey);
  }

  private applyPendingPlanSelectionFallback(
    subscriptionData: any,
    packageData: any,
  ): { subscriptionData: any; packageData: any } | null {
    if (subscriptionData.has_pending_plan_change) {
      return null;
    }

    const selection = this.loadPendingPlanSelection();
    if (!selection?.package_id) {
      return null;
    }

    if (String(selection.package_id) === String(packageData.package_id)) {
      return null;
    }

    const updatedSubscription = {
      ...subscriptionData,
      has_pending_plan_change: true,
      pending_package: {
        package_id: selection.package_id,
        package_name: selection.name,
        price: selection.price,
        billing_cycle: selection.billingCycle,
        currency: packageData.currency || 'INR',
        is_trial: false,
      },
      display_status: subscriptionData.status,
      display_payment_status: subscriptionData.payment_status,
    };

    return {
      subscriptionData: updatedSubscription,
      packageData,
    };
  }

  /**
   * Fallback when API cannot resolve last paid package from invoices yet.
   * Uses the snapshot saved before the user confirmed a plan change.
   */
  private applyLastPaidPackageFallback(
    subscriptionData: any,
    packageData: any,
  ): { subscriptionData: any; packageData: any } | null {
    const pendingStatuses = ['Pending', 'Failed', 'Overdue'];
    if (
      subscriptionData.has_pending_plan_change ||
      !pendingStatuses.includes(subscriptionData.payment_status)
    ) {
      return null;
    }

    const snapshot = this.loadLastPaidPackageSnapshot();
    if (!snapshot?.package_id) {
      return null;
    }

    if (String(snapshot.package_id) === String(packageData.package_id)) {
      return null;
    }

    const updatedSubscription = {
      ...subscriptionData,
      has_pending_plan_change: true,
      pending_package: {
        package_id: packageData.package_id,
        package_name: packageData.package_name,
        price: packageData.price,
        billing_cycle: packageData.billing_cycle,
        currency: packageData.currency,
        is_trial: packageData.is_trial || false,
      },
      display_status: 'Active',
      display_payment_status: 'Paid',
    };

    const updatedPackage = {
      package_id: snapshot.package_id,
      package_name: snapshot.name,
      price: snapshot.price,
      billing_cycle: snapshot.billingCycle,
      currency: snapshot.currency,
      is_trial: false,
    };

    return {
      subscriptionData: updatedSubscription,
      packageData: updatedPackage,
    };
  }

  private calculatePeriodEnd(
    startDate: Date,
    billingCycle: 'monthly' | 'yearly',
  ): Date {
    const endDate = new Date(startDate);
    if (billingCycle === 'monthly') {
      endDate.setMonth(endDate.getMonth() + 12);
    } else {
      endDate.setFullYear(endDate.getFullYear() + 1);
    }
    return endDate;
  }

  getCurrentPlanDisplayPrice(): number {
    if (!this.currentPackage) {
      return 0;
    }

    const price = Number(this.currentPackage.price) || 0;
    const isYearly = this.currentPackage.billingCycle?.toLowerCase() === 'yearly';
    return isYearly ? price / 12 : price;
  }

  getPendingPlanDisplayPrice(plan?: SubscriptionPlan | null): number {
    const target = plan || this.pendingPlanChange;
    if (!target) {
      return 0;
    }

    return this.selectedBillingCycle === 'monthly'
      ? target.monthlyPrice
      : target.yearlyPrice;
  }

  getPlanChangeTimingDescription(): string {
    if (this.planChangeTiming === 'next_cycle' && this.currentPackage?.nextBillingDate) {
      if (!this.canSchedulePlanChangeForNextCycle()) {
        return 'Your next billing date is today or has passed. Use Immediate Purchase to change your plan now.';
      }
      return `Your current package stays active until ${new Date(
        this.currentPackage.nextBillingDate,
      ).toLocaleDateString('en-GB')}. The payment invoice for the new package will be sent on that date.`;
    }

    return 'Your new package will start immediately from today after payment.';
  }

  canSchedulePlanChangeForNextCycle(): boolean {
    if (!this.currentPackage?.nextBillingDate) {
      return false;
    }

    const billingDate = new Date(this.currentPackage.nextBillingDate);
    if (Number.isNaN(billingDate.getTime())) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    billingDate.setHours(0, 0, 0, 0);

    return billingDate.getTime() > today.getTime();
  }

  private getApiErrorMessage(err: unknown, fallback: string): string {
    const httpError =
      err instanceof HttpErrorResponse
        ? err
        : err &&
            typeof err === 'object' &&
            'error' in err &&
            'status' in err
          ? (err as HttpErrorResponse)
          : null;

    if (httpError) {
      const payload = parseApiErrorPayload(httpError);
      const message = payload?.['message'];
      if (typeof message === 'string' && message.trim()) {
        return message;
      }

      const nested = httpError.error;
      if (typeof nested === 'object' && nested !== null && 'message' in nested) {
        const nestedMessage = (nested as { message?: unknown }).message;
        if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
          return nestedMessage;
        }
      }
    }

    if (err && typeof err === 'object' && 'message' in err) {
      const message = (err as { message?: unknown }).message;
      if (
        typeof message === 'string' &&
        message.trim() &&
        !message.startsWith('Http failure response')
      ) {
        return message;
      }
    }

    return fallback;
  }

  isPlanUpgrade(plan: SubscriptionPlan): boolean {
    if (!this.currentPackage) return false;
    const currentPrice = Number(this.currentPackage.price) || 0;
    const isYearly = this.currentPackage.billingCycle?.toLowerCase() === 'yearly';
    const currentMonthlyPrice = isYearly ? currentPrice / 12 : currentPrice;
    
    return plan.monthlyPrice > (currentMonthlyPrice + 0.01);
  }

  isPlanDowngrade(plan: SubscriptionPlan): boolean {
    if (!this.currentPackage) return false;
    const currentPrice = Number(this.currentPackage.price) || 0;
    const isYearly = this.currentPackage.billingCycle?.toLowerCase() === 'yearly';
    const currentMonthlyPrice = isYearly ? currentPrice / 12 : currentPrice;
    
    return plan.monthlyPrice < (currentMonthlyPrice - 0.01);
  }

  isUpdateMode = false;

  // Purchase Plan Methods (for users without active subscription)
  openPurchaseModal(): void {
    this.showPurchaseModal = true;
    this.selectedPlan = null;
    this.selectedBillingCycle = 'monthly';
    this.isUpdateMode = false;
    this.loadSubscriptionPlan();
  }

  openUpdatePlanModal(): void {
    this.showPurchaseModal = true;
    this.selectedPlan = null;
    this.selectedBillingCycle = 'monthly';
    this.isUpdateMode = true;
    this.loadSubscriptionPlan();
  }

  closePurchaseModal(): void {
    this.showPurchaseModal = false;
    this.selectedPlan = null;
    this.isUpdateMode = false;
  }

  confirmPurchase(plan?: SubscriptionPlan | null): void {
    if (!plan) return;

    this.isPaymentLoading = true;
    this.currentPaymentPackageName = plan.name;

    const now = new Date();
    let trialEnd: Date | null = null;

    if (plan.isTrial) {
      const days = plan.trialPeriod || 14;
      const endDate = new Date(now);
      endDate.setDate(endDate.getDate() + days);
      trialEnd = endDate;
    }

    const payload: any = {
      client_id: this.localStorageService.getClientId(),
      package_id: plan.id,
      quantity: 1,
      status: plan.isTrial ? 'Active' : 'Inactive',
      billing_type: 'Recurring',
      billing_mode: 'invoice',
      billing_cycle: this.selectedBillingCycle,
      billing_occurrences: this.selectedBillingCycle === 'monthly' ? 12 : 1,
      current_period_start: now,
      current_period_end: this.calculatePeriodEnd(now, this.selectedBillingCycle),
      payment_status: 'Pending',
      is_trial: plan.isTrial,
      trial_start: plan.isTrial ? now : null,
      trial_end: trialEnd,
      cancel_at_period_end: false,
      grace_period_days: 0,
      reminder_before_days: 7,
    };

    if (this.isUpdateMode && this.currentPackage?.package_subscription_id) {
      payload.id = this.currentPackage.package_subscription_id;
      console.log('Updating package subscription with payload:', payload);

      this.subscriptionService.updatePackageSubscription(payload).subscribe({
        next: (res: any) => {
          console.log('Package subscription update response:', res);
          const subscriptionId =
            res?.id || res?.data?.id || this.currentPackage.package_subscription_id;

          if (subscriptionId) {
            this.completePackagePurchase(subscriptionId);
            this.closePurchaseModal();
          } else {
            this.toastr.error(res?.message || 'Failed to update subscription');
            this.isPaymentLoading = false;
          }
        },
        error: (err: any) => {
          console.error('Error updating package subscription:', err);
          this.toastr.error(err.message || 'Failed to update subscription');
          this.isPaymentLoading = false;
        },
      });
    } else {
      console.log('Creating package subscription with payload:', payload);

      this.subscriptionService.createPackageSubscription(payload).subscribe({
        next: (res: any) => {
          console.log('Package subscription response:', res);
          
          // Extract subscription ID (handle possible response structures)
          const subscriptionId = res?.id || res?.data?.id;
  
          if (subscriptionId) {
            // Call proforma invoice API
            this.completePackagePurchase(subscriptionId);
            this.closePurchaseModal();
          } else {
            this.toastr.error(res?.message || 'Failed to generate subscription');
            this.isPaymentLoading = false;
          }
        },
        error: (err: any) => {
          console.error('Error creating package subscription:', err);
          this.toastr.error(err.message || 'Failed to generate subscription');
          this.isPaymentLoading = false;
        }
      });
    }
  }

  // Set active tab for a specific plan
  setPlanTab(plan: SubscriptionPlan, tab: 'features' | 'limits'): void {
    plan.activeTab = tab;
  }

  // Set active tab for a specific addon
  setAddonTab(addon: Addon, tab: 'features' | 'credits'): void {
    addon.activeTab = tab;
  }

  // Simplify unit type for better user experience
  simplifyUnitType(unitType: string): string {
    if (!unitType) return '';
    //remove underscrom from string and convert to title case
    unitType = unitType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return unitType;
  }
  getUnitLableByUnit(unit:any){
   let  unitTypeOptions = [
        { label: 'Users', value: 'users' },
        { label: 'GB (Storage)', value: 'GB' },
        { label: 'Campaigns', value: 'campaigns' },
        { label: 'Templates', value: 'templates' },
        { label: 'Leads', value: 'leads' },
        { label: 'Contacts', value: 'contacts' },
        { label: 'Deals', value: 'deals' },
        { label: 'Invoices', value: 'invoices' },
        { label: 'Quotes', value: 'quotes' },
        { label: 'Contact Groups', value: 'contact_groups' },
        { label: 'Group Contacts', value: 'group_contacts' },
        { label: 'Email Sends', value: 'email_sends' },
        { label: 'SMS Sends', value: 'sms_sends' },
        { label: 'WhatsApp Marketing Messages', value: 'whatsapp_marketing_messages' },
        { label: 'Tasks', value: 'tasks' },
        { label: 'Projects', value: 'projects' },
        { label: 'Pipelines', value: 'pipelines' },
        { label: 'Custom Fields', value: 'custom_fields' },
        { label: 'Workflows', value: 'workflows' },
        { label: 'Automations', value: 'automations' },
        { label: 'Reports', value: 'reports' },
        { label: 'Dashboards', value: 'dashboards' },
        { label: 'API Calls', value: 'api_calls' },
        { label: 'Integrations', value: 'integrations' },
        { label: 'Team Members', value: 'team_members' },
        { label: 'Products', value: 'products' },
        { label: 'Services', value: 'services' },
        { label: 'Tickets', value: 'tickets' },
        { label: 'Support', value: 'support' },
        { label: 'Knowledge Base Articles', value: 'kb_articles' },
        { label: 'Email Accounts', value: 'email_accounts' },
        { label: 'Phone Numbers', value: 'phone_numbers' },
        { label: 'Documents', value: 'documents' },
        { label: 'Attachments (MB)', value: 'attachments_mb' },
        { label: 'Meetings', value: 'meetings' },
        { label: 'Appointments', value: 'appointments' },
        { label: 'Notes', value: 'notes' },
        { label: 'Tags', value: 'tags' },
        { label: 'Segments', value: 'segments' },
        { label: 'Landing Pages', value: 'landing_pages' },
        { label: 'Forms', value: 'forms' },
        { label: 'Surveys', value: 'surveys' },
        { label: 'Email Templates', value: 'email_templates' },
        { label: 'SMS Templates', value: 'sms_templates' },
        { label: 'WhatsApp Templates', value: 'whatsapp_templates' },
        { label: 'Broadcast Campaigns', value: 'broadcast_campaigns' },
        { label: 'Drip Campaigns', value: 'drip_campaigns' },
        { label: 'A/B Tests', value: 'ab_tests' },
        { label: 'Web Forms', value: 'web_forms' },
        { label: 'Chatbots', value: 'chatbots' },
        { label: 'Live Chat Sessions', value: 'live_chat_sessions' }
    ];
    return unitTypeOptions.find((option) => option.value === unit)?.label || unit;

  }
  fnLoadAllAddonSubscription(){
    this.isLoadingAddons = true;
    const payload = {
      "where": {
        "client_id":this.localStorageService.getClientId()
      },
      "filter": null,
      "page": 0,
      "limit": 25,
      "order": {
        "col": "updatedAt",
        "dir": "DESC"
      }
    }
    this.subscriptionService.getAllAddonSubscription(payload).subscribe({
      next: (res: any) => {
        console.log("addon_active_response", res);
        
        if (res && res.rows && Array.isArray(res.rows)) {
          // Map the response to extract addon details
          this.activeAddons = res.rows.map((subscription: any) => {
            const addon = subscription.package_addon || {};
            return {
              id: subscription.id,
              client_id: subscription.client_id,
              subscription_id: subscription.subscription_id,
              package_subscription_id: subscription.package_subscription_id,
              name: addon.addon_name || addon.name,
              addon_name: addon.addon_name,
              description: addon.description,
              price: addon.price,
              icon: this.getAddonIcon(addon),
              feature: addon.feature || { fixed: [], limits: [] },
              next_billing_at: subscription.next_billing_at,
              status: subscription.status,
              payment_status: subscription.payment_status,
              billing_type: subscription.billing_type,
              createdAt: subscription.createdAt,
              quantity: subscription.quantity,
              package_addon: addon // Keep the full addon object for reference
            };
          });
          console.log("activeAddons mapped:", this.activeAddons);
        } else {
          this.activeAddons = [];
        }
        this.isLoadingAddons = false;
      },
      error: (err:any) => {
        console.error('Error loading active addons:', err);
        this.toastr.error(err.message || 'Failed to load active addons');
        this.activeAddons = [];
        this.isLoadingAddons = false;
      }
    })
  }
  fnPurchaseAddon(addonData: any){
    console.log("addonData", addonData);
    
    const payload = {
      addon_subscription_id: addonData.id,
      client_id:  addonData.client_id
    }
    this.subscriptionService.proformaAddonInvoice(payload).subscribe({
      next: (res: any) => {
        if (res && res.payment_link) {
          this.paymentDetails = res;
          this.isPaymentLoading = false;
        } else {
          const msg = res?.message || 'Failed to generate addon payment link';
          this.toastr.error(msg);
          this.closePaymentModal();
        }
      },
      error: (err: any) => {
        console.error(err);
        const msg = err?.error?.message || err?.message || 'Something went wrong while initiating purchase';
        this.toastr.error(msg);
        this.closePaymentModal();
      }
    }); 
  }

  /**
   * After package purchase/upgrade, refresh role-based permissions and feature modules
   * so the UI immediately reflects the newly purchased package.
   */
  private refreshRoleAndPermissions(): void {
    const clientId = this.localStorageService.getClientId();
    const roleId = localStorage.getItem('role_id');

    console.log('[Billing] refreshRoleAndPermissions called | clientId:', clientId, '| roleId:', roleId);

    if (!clientId || !roleId) {
      console.warn('[Billing] ⚠️ Missing clientId or roleId — skipping permission refresh');
      return;
    }

    const payload = {
      where: {
        client_id: parseInt(clientId, 10)
      }
    };

    this.usersService.getAllRoles(payload).subscribe({
      next: (res: any) => {
        console.log('[Billing] getAllRoles API response:', res);

        if (!res || !Array.isArray(res)) {
          console.warn('[Billing] ⚠️ Unexpected response format from getAllRoles');
          return;
        }

        const currentRole = res.find((r: any) => String(r.role_id) === String(roleId));
        console.log('[Billing] Matched role for roleId', roleId, ':', currentRole);

        if (!currentRole) {
          console.warn('[Billing] ⚠️ No matching role found for roleId:', roleId);
          return;
        }

        // Update local storage with new permissions and feature modules
        if (currentRole.permissions) {
          localStorage.setItem('permissions', JSON.stringify(currentRole.permissions));
          console.log('[Billing] ✅ localStorage.permissions updated:', currentRole.permissions);
        } else {
          console.warn('[Billing] ⚠️ No permissions found on role object');
        }

        if (currentRole.client?.feature_modules) {
          localStorage.setItem(
            'feature_modules',
            typeof currentRole.client.feature_modules === 'string'
              ? currentRole.client.feature_modules
              : JSON.stringify(currentRole.client.feature_modules),
          );
          console.log('[Billing] ✅ localStorage.feature_modules updated:', currentRole.client.feature_modules);
        } else {
          console.warn('[Billing] ⚠️ No feature_modules found on role.client — check API response structure');
        }

        // Ask PermissionService to reload from local storage
        this.permissionService.refresh();
        console.log('[Billing] ✅ PermissionService refreshed successfully');
        this.subscriptionBillingLockService.forceRefresh().subscribe({
          next: () => {
            if (!this.subscriptionBillingLockService.isBillingRouteLockActive()) {
              this.router.navigate(['/main/leads']);
            }
          },
          error: () => { /* keep user on billing if refetch fails */ }
        });
      },
      error: (err: any) => {
        console.error('[Billing] ❌ Failed to refresh roles/permissions after purchase', err);
      }
    });
  }
}

